import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router";
import { useGlobalStore } from "~/stores/store-provider";
import {
  FaArrowLeft,
  FaCheck,
  FaTimes,
  FaKey,
  FaInfoCircle,
  FaGlobe,
} from "react-icons/fa";
import { SiMetabase } from "react-icons/si";

type MetabaseFormData = {
  name: string;
  metabaseUrl: string;
  apiKey: string;
};

type FormErrors = {
  [key in keyof MetabaseFormData]?: string;
};

const MetabaseConnection = () => {
  const navigate = useNavigate();
  const { addConnection } = useGlobalStore((state) => state);
  const timersRef = useRef<Set<NodeJS.Timeout>>(new Set());

  const [formData, setFormData] = useState<MetabaseFormData>({
    name: "",
    metabaseUrl: "",
    apiKey: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<"success" | "error">("success");

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(timer => clearTimeout(timer));
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validate connection name
    if (!formData.name.trim()) {
      newErrors.name = "Connection name is required";
    } else if (formData.name.length < 3 || formData.name.length > 50) {
      newErrors.name = "Must be between 3 and 50 characters";
    }

    // Validate Metabase URL
    if (!formData.metabaseUrl.trim()) {
      newErrors.metabaseUrl = "Metabase URL is required";
    } else {
      try {
        const url = new URL(formData.metabaseUrl);
        if (!url.protocol.startsWith("http")) {
          newErrors.metabaseUrl = "URL must start with http:// or https://";
        }
      } catch {
        newErrors.metabaseUrl = "Please enter a valid URL";
      }
    }

    // Validate API Key
    if (!formData.apiKey.trim()) {
      newErrors.apiKey = "API Key is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare connection data
      const connectionData = {
        name: formData.name,
        connection_type: "metabase",
        metabase_url: formData.metabaseUrl,
        api_key: formData.apiKey,
      };

      await addConnection(connectionData);

      // Show success toast before navigating
      setToastType("success");
      setToastMessage("Metabase connected successfully!");
      setShowToast(true);

      const timer = setTimeout(() => {
        navigate("/connections");
        timersRef.current.delete(timer);
      }, 1500);
      timersRef.current.add(timer);
    } catch (error) {
      console.error("Failed to save connection:", error);

      // Show error toast
      setToastType("error");
      setToastMessage("Failed to save connection. Please try again.");
      setShowToast(true);

      const timer = setTimeout(() => {
        setShowToast(false);
        timersRef.current.delete(timer);
      }, 2000);
      timersRef.current.add(timer);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full bg-base-200/50 overflow-y-auto">
      <div className="container mx-auto px-4 py-8 max-w-2xl min-h-full">
        {/* Back Navigation */}
        <button
          onClick={() => navigate("/connections")}
          className="btn btn-ghost btn-sm gap-2 mb-6"
        >
          <FaArrowLeft className="w-3 h-3" />
          Back to Connections
        </button>

        {/* Main Card */}
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body p-8">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-base-200">
              <div className="p-3 bg-purple-500/10 rounded-lg">
                <SiMetabase className="w-7 h-7 text-purple-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Connect Metabase Instance</h1>
                <p className="text-sm text-base-content/60">
                  Import your analytics and insights from Metabase
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Connection Name */}
              <div className="form-control w-full">
                <label className="label mb-2">
                  <span className="label-text font-medium">
                    Connection Name
                  </span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Analytics Metabase"
                  className={`input input-bordered w-full ${
                    errors.name ? "input-error" : ""
                  }`}
                />
                {errors.name && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.name}
                    </span>
                  </label>
                )}
              </div>

              {/* Metabase URL */}
              <div className="form-control w-full">
                <label className="label mb-2">
                  <span className="label-text font-medium">Metabase URL</span>
                  <div
                    className="tooltip"
                    data-tip="The full URL of your Metabase instance"
                  >
                    <FaInfoCircle className="w-3 h-3 text-base-content/50" />
                  </div>
                </label>
                <label className="input input-bordered flex items-center gap-2 w-full">
                  <FaGlobe className="w-4 h-4 text-base-content/50" />
                  <input
                    type="url"
                    name="metabaseUrl"
                    value={formData.metabaseUrl}
                    onChange={handleInputChange}
                    placeholder="https://metabase.example.com"
                    className={`grow ${errors.metabaseUrl ? "text-error" : ""}`}
                  />
                </label>
                {errors.metabaseUrl && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.metabaseUrl}
                    </span>
                  </label>
                )}
              </div>

              {/* API Key */}
              <div className="form-control w-full">
                <label className="label mb-2">
                  <span className="label-text font-medium">API Key</span>
                  <div
                    className="tooltip"
                    data-tip="Generate from Metabase Admin → Settings → API Keys"
                  >
                    <FaInfoCircle className="w-3 h-3 text-base-content/50" />
                  </div>
                </label>
                <label className="input input-bordered flex items-center gap-2 w-full">
                  <FaKey className="w-4 h-4 text-base-content/50" />
                  <input
                    type="password"
                    name="apiKey"
                    value={formData.apiKey}
                    onChange={handleInputChange}
                    placeholder="mb_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="grow font-mono text-sm"
                  />
                </label>
                {errors.apiKey && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.apiKey}
                    </span>
                  </label>
                )}
              </div>

              {/* Info Alert */}
              <div className="alert bg-base-200/50 border-0">
                <FaInfoCircle className="w-4 h-4" />
                <div className="text-sm">
                  <p className="font-medium">What happens next?</p>
                  <p className="text-base-content/70">
                    After connecting, you'll be able to import your Metabase
                    questions, dashboards, and collections into Gamgee.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-base-200">
                <button
                  type="button"
                  onClick={() => navigate("/connections")}
                  className="btn btn-ghost"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn btn-primary"
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Connecting...
                    </>
                  ) : (
                    "Save Connection"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Help Link */}
        <div className="text-center mt-6">
          <a
            href="https://www.metabase.com/docs/latest/api-documentation"
            target="_blank"
            rel="noopener noreferrer"
            className="link link-primary text-sm"
          >
            Need help? View Metabase API documentation →
          </a>
        </div>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div className="toast toast-bottom toast-center">
          <div className={`alert ${toastType === 'success' ? 'alert-success' : 'alert-error'}`}>
            {toastType === 'success' ? (
              <FaCheck className="w-4 h-4" />
            ) : (
              <FaTimes className="w-4 h-4" />
            )}
            <span>{toastMessage}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetabaseConnection;
